/**
 * The machine on the setup screen: seven lines, one per setup check, filled first from the provisioning run's steps
 * and then from the checks the API merges in. Plus the strip's progress and a rough time left. Pure.
 */
import { CHECK_KEYS, type Check, type CheckKey, type FounderRun, type FounderWorld, type Step } from './schemas'

export type LineStatus = 'ok' | 'running' | 'waiting' | 'failing'

export interface MachineLine {
  key: string
  label: string
  status: LineStatus
  /** When it turned ok (or last changed), if known. */
  at: string | null
}

/** Founder-facing names. Status lines say what the founder sees, never the tool behind it (docs/brand.md). */
export const CHECK_LABELS: Record<CheckKey, string> = {
  machine: 'Machine created',
  system: 'System ready',
  address: 'Your address is live',
  tools: 'Tools installed',
  user: 'Your user created',
  phone: 'Phone link ready',
  starter: 'Starter app ready',
}

/**
 * Expected seconds per check, for the estimate. A plain table for now (about five minutes in all); later it comes
 * from measured durations over recent provisions.
 */
export const EXPECTED_SECONDS: Record<CheckKey, number> = {
  machine: 40,
  system: 35,
  address: 45,
  tools: 120,
  user: 10,
  phone: 20,
  starter: 30,
}
const UNKNOWN_SECONDS = 30
/** The current line never counts for less than this, so an overrun reads "less than a minute", not zero. */
const MIN_CURRENT_SECONDS = 15

const isKnown = (key: string): key is CheckKey => (CHECK_KEYS as readonly string[]).includes(key)

/** Known keys in the machine's order, then any others as they came. A repeated key keeps its last report. */
export function orderChecks(checks: Check[]): Check[] {
  const byKey = new Map<string, Check>()
  for (const c of checks) byKey.set(c.key, c)
  const known = CHECK_KEYS.flatMap((k) => (byKey.has(k) ? [byKey.get(k)!] : []))
  const rest = [...byKey.values()].filter((c) => !isKnown(c.key))
  return [...known, ...rest]
}

type Derived = { status: LineStatus; at: string | null }

const SETTLED = new Set<Step['status']>(['done', 'manual', 'skipped'])

function fromStep(step: Step | undefined): Derived | null {
  if (!step) return null
  if (SETTLED.has(step.status)) return { status: 'ok', at: step.finishedAt }
  if (step.status === 'running') return { status: 'running', at: step.startedAt }
  if (step.status === 'failed') return { status: 'failing', at: step.finishedAt }
  return null
}

/** Which line each provisioning step feeds, so a stalled or failed step shows on the line the founder sees. */
const STEP_LINE: Record<string, CheckKey> = {
  tags: 'machine',
  firewall: 'machine',
  box: 'machine',
  boxActive: 'system',
  repoFromStarter: 'starter',
  attachRepo: 'starter',
  railwayProject: 'address',
  railwayService: 'address',
  appToken: 'address',
  railwayDomain: 'address',
  attachDeploy: 'address',
  deploy: 'address',
  customDomain: 'address',
  dns: 'address',
  https: 'address',
  kindeCallbacks: 'address',
  cloneRepo: 'starter',
}
/** Lines the agent confirms: only a failed step carries over; running or done steps say nothing yet. */
const AGENT_CONFIRMS = new Set<CheckKey>(['starter'])

/** What the run's steps say about a check before the API's checks do. 'hide' drops the line. */
function fromSteps(run: FounderRun | null, key: CheckKey): Derived | 'hide' | null {
  if (!run) return null
  if (key === 'address') {
    const https = run.steps.find((s) => s.key === 'https')
    // No subdomain, or a computer with no app: there's no address line to show.
    if (run.template === 'boxOnly' || (https?.status === 'skipped' && https.ids.reason === 'no subdomain')) return 'hide'
  }
  const parts = run.steps.filter((s) => STEP_LINE[s.key] === key).map((s) => fromStep(s))
  if (!parts.length) return null
  const failed = parts.find((p) => p?.status === 'failing')
  if (failed) return failed
  // The agent confirms these; until it does, a step's progress says nothing the founder can use.
  if (AGENT_CONFIRMS.has(key)) return null
  const running = parts.find((p) => p?.status === 'running')
  if (running) return running
  if (parts.every((p) => p?.status === 'ok')) return { status: 'ok', at: parts[parts.length - 1]!.at }
  // Some steps settled, the rest still to come: under way.
  if (parts.some((p) => p?.status === 'ok')) return { status: 'running', at: null }
  return null
}

/** Whether setup has begun: a provisioning run exists, or the agent has reported. */
export function hasStarted(world: Pick<FounderWorld, 'run' | 'checks'>): boolean {
  return world.run !== null || world.checks.length > 0
}

/**
 * The machine's lines. A check that is ok or failing wins; otherwise the run's steps speak; otherwise it waits. Once
 * setup has started, the first line that isn't settled shows as running, so there is always a "now" line.
 */
export function machineLines(world: Pick<FounderWorld, 'run' | 'checks'>): MachineLine[] {
  const checks = orderChecks(world.checks)
  const byKey = new Map(checks.map((c) => [c.key, c]))
  const lines: MachineLine[] = []
  for (const key of CHECK_KEYS) {
    const c = byKey.get(key)
    if (c && c.status !== 'pending') {
      lines.push({ key, label: CHECK_LABELS[key], status: c.status === 'ok' ? 'ok' : 'failing', at: c.at ?? null })
      continue
    }
    const d = fromSteps(world.run, key)
    if (d === 'hide') continue
    if (d) lines.push({ key, label: CHECK_LABELS[key], status: d.status, at: d.at })
    else lines.push({ key, label: CHECK_LABELS[key], status: 'waiting', at: null })
  }
  for (const c of checks) {
    if (isKnown(c.key)) continue
    lines.push({ key: c.key, label: c.label, status: c.status === 'ok' ? 'ok' : c.status === 'failing' ? 'failing' : 'waiting', at: c.at ?? null })
  }
  if (hasStarted(world) && !lines.some((l) => l.status === 'running' || l.status === 'failing')) {
    const first = lines.find((l) => l.status === 'waiting')
    if (first) first.status = 'running'
  }
  return lines
}

export interface Progress {
  started: boolean
  total: number
  done: number
  ready: boolean
  failing: boolean
  /** The line the strip names: the first one that isn't ok. */
  current: MachineLine | null
  /** Rough seconds left, or null when there's nothing to estimate. */
  remainingSeconds: number | null
}

const ms = (iso: string | null | undefined) => (iso ? Date.parse(iso) : NaN)

/** When the provisioning run began: its createdAt, else its first step's start. */
export function runStartedAt(run: FounderRun | null): string | null {
  if (!run) return null
  if (run.createdAt) return run.createdAt
  const starts = run.steps.map((s) => s.startedAt).filter((s): s is string => Boolean(s)).sort()
  return starts[0] ?? null
}

/**
 * Done, total and time left. Time left is the expected time of every line still to go, with the current line's
 * share reduced by how long it has been running (since the last line turned ok, or since the run began).
 */
export function progress(lines: MachineLine[], opts: { now: number; startedAt: string | null; started: boolean }): Progress {
  const total = lines.length
  const done = lines.filter((l) => l.status === 'ok').length
  const ready = total > 0 && done === total
  const failing = lines.some((l) => l.status === 'failing')
  const current = lines.find((l) => l.status !== 'ok') ?? null
  if (!opts.started || ready) return { started: opts.started, total, done, ready, failing, current, remainingSeconds: ready ? 0 : null }

  const expected = (l: MachineLine) => (isKnown(l.key) ? EXPECTED_SECONDS[l.key] : UNKNOWN_SECONDS)
  const okTimes = lines.filter((l) => l.status === 'ok').map((l) => ms(l.at)).filter((t) => !Number.isNaN(t))
  const since = okTimes.length ? Math.max(...okTimes) : ms(opts.startedAt)
  const elapsed = Number.isNaN(since) ? 0 : Math.max(0, (opts.now - since) / 1000)
  let remaining = 0
  for (const l of lines) {
    if (l.status === 'ok') continue
    remaining += l === current ? Math.max(expected(l) - elapsed, MIN_CURRENT_SECONDS) : expected(l)
  }
  return { started: true, total, done, ready, failing, current, remainingSeconds: Math.round(remaining) }
}

/** "about 3 minutes left", "about a minute left", "less than a minute left". */
export function timeLeft(seconds: number): string {
  if (seconds < 60) return 'less than a minute left'
  const min = Math.round(seconds / 60)
  return min <= 1 ? 'about a minute left' : `about ${min} minutes left`
}

/** The strip's headline. A failing check never shows its error to a founder. */
export function stripHeadline(p: Progress): string {
  if (!p.started) return 'Your computer starts when you pick a region'
  if (p.ready) return 'Your computer is ready'
  if (p.failing) return 'Taking longer than usual · we\'re on it'
  const left = p.remainingSeconds === null ? '' : ` · ${timeLeft(p.remainingSeconds)}`
  return `Setting up your computer · ${p.done} of ${p.total}${left}`
}

/** Whether a given check is ok, from the merged view (steps count for step-backed checks). */
export function lineOk(lines: MachineLine[], key: string): boolean {
  return lines.some((l) => l.key === key && l.status === 'ok')
}
